import moment from 'moment-timezone';
import { Timestamp } from 'firebase/firestore';

interface TimestampLike {
  toDate: () => Date;
  seconds: number;
  nanoseconds: number;
}

interface TimestampSeconds {
  seconds: number;
  nanoseconds: number;
}

export type DateInput = Date | string | Timestamp | TimestampLike | TimestampSeconds;

const isFirestoreTimestamp = (timestamp: any): timestamp is TimestampLike => {
  return timestamp && typeof timestamp.toDate === 'function';
};

const isTimestampSeconds = (timestamp: any): timestamp is TimestampSeconds => {
  return timestamp && typeof timestamp.seconds === 'number';
};

/**
 * Converts any date input to a moment object in the specified timezone
 */
export const toMoment = (date: DateInput, timezone: string = 'UTC') => {
  if (!date) return null;

  try {
    // Handle Firestore Timestamp
    if (isFirestoreTimestamp(date)) {
      return moment(date.toDate()).tz(timezone);
    }

    // Handle timestamp objects with seconds
    if (isTimestampSeconds(date)) {
      return moment(new Date(date.seconds * 1000)).tz(timezone);
    }

    // Handle string — if no timezone suffix assume UTC to avoid browser local-time misparse
    if (typeof date === 'string') {
      const normalized = /Z$|[+-]\d{2}:?\d{2}$/.test(date) ? date : date + 'Z';
      return moment(normalized).tz(timezone);
    }

    // Handle Date
    return moment(date).tz(timezone);
  } catch (e) {
    return null;
  }
};

/**
 * Formats a date with timezone
 * @param format Default: 'MMM D, YYYY h:mm A z'
 */
export const formatDate = (
  date: DateInput,
  timezone: string = 'UTC',
  format: string = 'MMM D, YYYY h:mm A z'
) => {
  const momentDate = toMoment(date, timezone);
  return momentDate ? momentDate.format(format) : '-';
};

/**
 * Gets start and end of day in UTC for a given date in org timezone
 */
export const getDayRange = (date: Date, timezone: string = 'UTC') => {
  const momentDate = moment.tz(date, timezone);
  const start = momentDate.clone().startOf('day');
  const end = momentDate.clone().endOf('day');

  return {
    start: start.utc(),
    end: end.utc(),
  };
};

/**
 * Creates Firestore timestamp from a date
 */
export const toFirestoreTimestamp = (date: Date | moment.Moment) => {
  const momentDate = moment.isMoment(date) ? date : moment(date);
  return Timestamp.fromDate(momentDate.toDate());
};

/**
 * Gets date components in org timezone
 */
export const getDateComponents = (date: Date, timezone: string = 'UTC') => {
  const momentDate = moment.tz(date, timezone);
  return {
    year: momentDate.year(),
    month: momentDate.month(),
    date: momentDate.date(),
    hour: momentDate.hour(),
    minute: momentDate.minute(),
  };
};

/**
 * Creates a date in org timezone from components
 */
export const fromDateComponents = (
  components: { year: number; month: number; date: number },
  timezone: string = 'UTC'
) => {
  return moment.tz([components.year, components.month, components.date], timezone);
};

/**
 * Gets minimum allowed date (today) in org timezone
 */
export const getMinDate = (timezone: string = 'UTC') => {
  // Get current time in org timezone
  const orgTime = moment().tz(timezone);

  // If it's after business hours in org timezone, use tomorrow as min date
  if (orgTime.hour() >= 19) {
    // Get tomorrow in org timezone
    const tomorrow = orgTime.clone().add(1, 'day').startOf('day');

    // Create date at noon UTC to avoid timezone shifts
    const result = moment.utc(tomorrow.format('YYYY-MM-DD')).hours(12).toDate();
    return result;
  }

  // Get today in org timezone
  const today = orgTime.clone().startOf('day');

  // Create date at noon UTC to avoid timezone shifts
  const result = moment.utc(today.format('YYYY-MM-DD')).hours(12).toDate();
  return result;
};

/**
 * Gets available time slots between 8am-7pm in org timezone (hours only)
 */
export const getTimeSlots = (timezone: string = 'UTC') => {
  const slots: string[] = [];
  for (let hour = 8; hour < 19; hour++) {
    const time = moment.tz(timezone).hour(hour).minute(0);
    slots.push(time.format('HH:00'));
  }
  return slots;
};

/**
 * Gets minimum allowed time based on date in org timezone
 * If date is today: returns next hour if within allowed hours, null if after max time
 * If future date: returns 8am
 */
export const getMinTime = (date: Date, timezone: string = 'UTC') => {
  // Create moment objects directly in org timezone
  const now = moment().tz(timezone);
  const today = now.clone().startOf('day');

  // Convert input date to org timezone while preserving the local date components
  const dateComponents = {
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate(),
  };

  // Create date directly in org timezone using components
  const selectedDate = moment
    .tz([dateComponents.year, dateComponents.month, dateComponents.date], timezone)
    .startOf('day');

  if (!selectedDate) return '08:00';

  // If selected date is today in org timezone
  if (selectedDate.isSame(today, 'day')) {
    // Get current hour in org timezone
    const currentHour = now.hour();
    const currentMinute = now.minute();

    // If current time is after or equal to max time (7pm), return null
    if (currentHour >= 19 || (currentHour === 18 && currentMinute >= 59)) {
      return null;
    }

    // Calculate next hour while staying in org timezone
    const nextHour = currentHour + 1;

    // If next hour is before 8am, return 8am
    if (nextHour < 8) {
      return '08:00';
    }

    // If next hour would be after max time, return null
    if (nextHour >= 19) {
      return null;
    }

    // Format the next hour in 24-hour format
    const formattedHour = nextHour.toString().padStart(2, '0');
    return `${formattedHour}:00`;
  }

  // For future dates, always return 8am
  return '08:00';
};

/**
 * Gets maximum allowed time (7pm) in org timezone
 */
export const getMaxTime = (timezone: string = 'UTC') => {
  return '19:00';
};

/**
 * Formats a date without displaying the timezone in the output
 * The date is still converted to the specified timezone before formatting
 * @param format Default: 'MMMM D, YYYY'
 */
export const formatOptInDate = (dateValue?: string | null): string | null => {
  if (!dateValue) return null;

  const isoDateMatch = String(dateValue).match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDateMatch) {
    const [year, month, day] = isoDateMatch[1].split('-').map(Number);
    const utcDate = new Date(Date.UTC(year, month - 1, day));
    return utcDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  const legacyDateMatch = String(dateValue).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (legacyDateMatch) {
    const [, month, day, year] = legacyDateMatch;
    const utcDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return utcDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  return null;
};

export const formatDateOnly = (
  date: DateInput,
  timezone: string = 'UTC',
  format: string = 'MMMM D, YYYY'
) => {
  const momentDate = toMoment(date, timezone);
  return momentDate ? momentDate.format(format) : '-';
};

/**
 * Formats a date without any timezone conversion
 * This is useful for dates that should be displayed exactly as they are stored
 * @param format Default: 'MMMM D, YYYY'
 */
export const formatDateNoTimezone = (date: DateInput, format: string = 'MMMM D, YYYY') => {
  if (!date) return '-';

  try {
    // Handle Firestore Timestamp
    if (isFirestoreTimestamp(date)) {
      return moment(date.toDate()).format(format);
    }

    // Handle timestamp objects with seconds
    if (isTimestampSeconds(date)) {
      return moment(new Date(date.seconds * 1000)).format(format);
    }

    // Handle string or Date
    return moment(date).format(format);
  } catch (e) {
    return '-';
  }
};

/**
 * Converts a date range to Firestore timestamps in the specified timezone
 * @param dateRange Object with from and to dates
 * @param timezone Organization timezone
 * @returns Object with startTimestamp and endTimestamp as Firestore Timestamps
 */
export const getDateRangeTimestamps = (
  dateRange: { from: Date | null; to: Date | null },
  timezone: string = 'UTC'
) => {
  if (!dateRange.from || !dateRange.to) {
    return null;
  }

  const fromDate = toMoment(dateRange.from, timezone)!.startOf('day');
  const toDate = toMoment(dateRange.to, timezone)!.endOf('day');
  const startUtc = fromDate.clone().utc();
  const endUtc = toDate.clone().utc();
  const startTimestamp = toFirestoreTimestamp(startUtc);
  const endTimestamp = toFirestoreTimestamp(endUtc);

  return {
    startTimestamp,
    endTimestamp,
  };
};
